import { z } from 'zod';
import { ATTENDANCE_STATUSES, ROLES, SESSION_STATUSES } from './constants.js';

// --- primitives ------------------------------------------------------------

export const uuid = z.uuid();
/**
 * Calendar date, YYYY-MM-DD. Always resolved in the organization's timezone.
 *
 * z.iso.date() knows the calendar (no 30 February) but allows year 0000,
 * which Postgres has no date for: the insert failed where the parse had
 * passed, as a 500.
 */
export const isoDate = z.iso
  .date()
  .refine((value) => !value.startsWith('0000-'), 'must be a real date');
export const slug = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase alphanumeric with single hyphens');

export const attendanceStatus = z.enum(ATTENDANCE_STATUSES);
export const sessionStatus = z.enum(SESSION_STATUSES);
export const role = z.enum(ROLES);
/** Roles that can be assigned within an organization; super_admin is platform-level. */
export const orgRole = z.enum(['org_admin', 'teacher']);
export const weekday = z.int().min(0).max(6);

const password = z.string().min(12, 'must be at least 12 characters').max(200);
/** A wall-clock time, HH:MM. Shared with the CSV import so both read the same rule. */
export const HH_MM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const personName = z.string().trim().min(1).max(120);

// --- auth ------------------------------------------------------------------

/**
 * Signing in needs only an email and a password: the organization is inferred
 * from the account.
 *
 * Email is unique per organization rather than globally, so an address *can*
 * belong to two of them. That is resolved by verifying the password against
 * every candidate and asking only when more than one matches - see
 * `loginChoiceSchema`. Both narrowing fields are optional:
 *   orgSlug        - set by the /o/<slug>/login entry point
 *   organizationId - the answer to a previous ambiguity, null meaning platform
 */
/**
 * What someone types into the first login field: an email address for an
 * organization member, or a username for a platform account. Not validated as
 * an email, because a username is not one.
 */
export const loginIdentifier = z.string().trim().toLowerCase().min(1).max(320);

export const loginRequestSchema = z.object({
  identifier: loginIdentifier,
  password: z.string().min(1),
  orgSlug: slug.optional(),
  organizationId: uuid.nullable().optional(),
});

/** One option in the disambiguation list; a null id is platform administration. */
export const loginOrganizationSchema = z.object({
  id: uuid.nullable(),
  name: z.string(),
  slug: slug.nullable(),
});

/**
 * Returned when one address with one password matches accounts at several
 * organizations. Only ever sent after the password has been verified against
 * those accounts, so it discloses nothing to someone who does not hold them.
 */
export const loginChoiceSchema = z.object({
  needsOrganization: z.literal(true),
  organizations: z.array(loginOrganizationSchema).min(2),
});

export const adminLoginRequestSchema = z.object({
  identifier: loginIdentifier,
  password: z.string().min(1),
});

export const currentUserSchema = z.object({
  id: uuid,
  /** Null for a platform account created with a username instead. */
  email: z.email().nullable(),
  /** Set only on platform accounts. */
  username: z.string().nullable(),
  name: z.string(),
  role,
  organization: z.object({ id: uuid, name: z.string(), slug, timezone: z.string() }).nullable(),
});

// --- organizations ---------------------------------------------------------

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug,
  /** IANA zone. Drives what "today" means when marking attendance. */
  timezone: z.string().min(1).default('UTC'),
  admin: z.object({ email: z.email(), name: personName, password }).optional(),
});

export const updateOrganizationSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    timezone: z.string().min(1),
    isActive: z.boolean(),
  })
  .partial();

// --- users -----------------------------------------------------------------

/** An email, normalised the same way the login identifier is. */
const emailAddress = z.email().trim().toLowerCase().max(320);
/**
 * Platform accounts only. Globally unique, so it is deliberately narrower than
 * an email: no spaces, no '@', nothing that could be mistaken for one.
 */
const usernameValue = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/, 'letters, digits, dots, dashes and underscores');

export const createUserSchema = z.object({
  email: emailAddress,
  name: personName,
  role: orgRole,
  /**
   * Optional: omitted means the account is created without a usable password
   * and the administrator is handed a setup link to send instead.
   */
  password: password.optional(),
});

/**
 * Declared standalone rather than as `createUserSchema.partial()`. `.partial()`
 * keeps `.default()`, so an omitted field would be rewritten with its default
 * rather than left alone - the bug that silently detached every period from a
 * class when only its name was edited.
 */
/**
 * Deleting is a second stage past retiring, not a shortcut through it.
 *
 * `true` requires the record to be inactive already; `false` restores it and is
 * refused to anybody but a platform administrator. Both live on the existing
 * update route rather than on routes of their own, so they inherit its tenancy,
 * its audit block and its 404.
 *
 * Deliberately absent from platformUpdateUserSchema: a platform account carries
 * no organization_id, so the policy that hides deleted rows does not reach it.
 */
const isDeleted = z.boolean().optional();

export const updateUserSchema = z
  .object({
    name: personName,
    email: emailAddress,
    role: orgRole,
    isActive: z.boolean(),
    password,
  })
  .partial()
  .extend({ isDeleted });

// --- platform-level user administration ------------------------------------

/**
 * A super admin editing any account anywhere. Wider than the org-scoped schemas
 * in three ways: the organization is a field rather than the caller's context,
 * `username` exists, and `role` is the full set including super_admin.
 *
 * `organizationId: null` means a platform account. The database enforces the
 * two legal identity shapes; `assertIdentityShape` turns a violation into a
 * readable 400 before it gets that far.
 */
export const platformCreateUserSchema = z.object({
  organizationId: uuid.nullable(),
  email: emailAddress.optional(),
  username: usernameValue.optional(),
  name: personName,
  role,
  password: password.optional(),
});

export const platformUpdateUserSchema = z
  .object({
    name: personName,
    email: emailAddress.nullable(),
    username: usernameValue.nullable(),
    role,
    isActive: z.boolean(),
    password,
  })
  .partial();

// --- password setup links --------------------------------------------------

/** Redeeming a setup link. The token travels in the path, not the body. */
export const claimSetupSchema = z.object({ password });

// --- students --------------------------------------------------------------

export const createStudentSchema = z.object({
  firstName: personName,
  lastName: personName,
  dateOfBirth: isoDate.nullish(),
  guardianName: z.string().trim().max(200).nullish(),
  guardianContact: z.string().trim().max(200).nullish(),
});

export const updateStudentSchema = createStudentSchema.partial().extend({
  isActive: z.boolean().optional(),
  isDeleted,
});

// --- classes, periods, rosters ---------------------------------------------

export const createClassSchema = z.object({
  name: z.string().trim().min(1).max(200),
  periodIds: z.array(uuid).default([]),
  teacherIds: z.array(uuid).default([]),
});

/**
 * Declared standalone rather than as `createClassSchema.partial()`.
 *
 * `.partial()` makes a key optional but does NOT strip its `.default()`, so
 * deriving this from the create schema turned an absent `periodIds` into `[]`
 * - and a request that only renamed a class silently removed every period and
 * teacher from it. Omitted here means omitted.
 */
export const updateClassSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    periodIds: z.array(uuid),
    teacherIds: z.array(uuid),
    isActive: z.boolean(),
  })
  .partial()
  .extend({ isDeleted });

export const createPeriodSchema = z.object({
  name: z.string().trim().min(1).max(100),
  sortOrder: z.int().min(0).default(0),
  startTime: z.string().regex(HH_MM_PATTERN, 'must be HH:MM').nullish(),
  endTime: z.string().regex(HH_MM_PATTERN, 'must be HH:MM').nullish(),
});

/**
 * Retiring a period is `isActive: false`, and deleting a retired one is
 * `isDeleted: true`. Neither removes the row.
 *
 * Standalone for the same reason as updateClassSchema: inheriting `sortOrder`'s
 * default would reset the ordering on every unrelated edit.
 */
export const updatePeriodSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    sortOrder: z.int().min(0),
    startTime: z.string().regex(HH_MM_PATTERN, 'must be HH:MM').nullish(),
    endTime: z.string().regex(HH_MM_PATTERN, 'must be HH:MM').nullish(),
    isActive: z.boolean(),
  })
  .partial()
  .extend({ isDeleted });

/** One class-period membership, addressable by its own id. */
export const updateClassPeriodSchema = z.object({
  sortOrder: z.int().min(0),
});

export const addClassPeriodSchema = z.object({
  periodId: uuid,
  /**
   * Where the period sits among the class's register columns. Omit it to
   * append.
   *
   * This used to default to 0, which is the wrong default for an attach: it
   * made a period attached from anywhere other than the class page the FIRST
   * column of that class's register, silently reordering a register nobody was
   * looking at. The class page never hit it because it sends the whole ordered
   * array to PATCH /admin/classes/:id instead.
   */
  sortOrder: z.int().min(0).optional(),
});

export const addClassTeacherSchema = z.object({
  userId: uuid,
});

/** Cancelling a single held session, as opposed to a whole date. */
export const updateClassSessionSchema = z.object({
  status: sessionStatus,
});

export const exceptionQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  classId: uuid.optional(),
});

export const createEnrollmentSchema = z.object({
  studentId: uuid,
  classId: uuid,
  startDate: isoDate,
  endDate: isoDate.nullish(),
});

/**
 * Enrol in one request, from either end: several students onto one class, or
 * one student onto several classes.
 *
 * No endDate in either shape: a bulk enrolment is always an open one. Ending is
 * per student and carries its own date, because students leave one at a time
 * and on their own days.
 */
export const bulkEnrollmentSchema = z.union([
  z.object({
    classId: uuid,
    studentIds: z.array(uuid).min(1).max(200),
    startDate: isoDate,
  }),
  /**
   * The transpose: one student onto several classes, which is what a student's
   * own record page enrols from. The same insert with the pair the other way
   * round, so it shares the route rather than duplicating the transaction, the
   * visibility checks and the enrolled/skipped counting.
   */
  z.object({
    studentId: uuid,
    classIds: z.array(uuid).min(1).max(200),
    startDate: isoDate,
  }),
]);

/**
 * endDate is nullable rather than merely required, so that clearing it reopens
 * an enrolment. That is what makes ending one undoable; without it the only way
 * back is a second enrolment row with a new start date, which silently rewrites
 * when the student joined.
 */
export const endEnrollmentSchema = z.object({ endDate: isoDate.nullable() });

export const createScheduleSchema = z.object({
  classId: uuid,
  weekday,
  effectiveFrom: isoDate,
  effectiveTo: isoDate.nullish(),
});

/**
 * Ends a recurrence, or reopens one by clearing the end date.
 *
 * Only effectiveTo, because the day a pattern started is a fact about the past:
 * moving it would silently change which dates were ever expected. Correcting a
 * mistyped start is removing the row and adding it again.
 */
export const updateScheduleSchema = z.object({ effectiveTo: isoDate.nullable() });

export const createExceptionSchema = z.object({
  /** null means the exception applies org-wide, e.g. a public holiday. */
  classId: uuid.nullish(),
  date: isoDate,
  reason: z.string().trim().max(200),
});

// --- the grid --------------------------------------------------------------

export const gridQuerySchema = z.object({ date: isoDate });

export const gridPeriodSchema = z.object({
  id: uuid,
  name: z.string(),
  sortOrder: z.int(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  sessionStatus: sessionStatus.nullable(),
  /**
   * True when the period has been retired and is shown only because this date
   * already has marks against it. Retired periods do not appear on new dates.
   */
  isRetired: z.boolean(),
});

export const gridStudentSchema = z.object({
  id: uuid,
  firstName: z.string(),
  lastName: z.string(),
});

export const gridResponseSchema = z.object({
  class: z.object({ id: uuid, name: z.string() }),
  date: isoDate,
  /** Whether the class's recurrence says it should meet on this date. */
  isScheduledDay: z.boolean(),
  /** Set when a schedule exception cancels this date. */
  exceptionReason: z.string().nullable(),
  periods: z.array(gridPeriodSchema),
  students: z.array(gridStudentSchema),
  /** Sparse map keyed `"<studentId>:<periodId>"`. Absent key = not yet marked. */
  cells: z.record(z.string(), attendanceStatus),
  canEdit: z.boolean(),
});

export const gridMarkSchema = z.object({
  studentId: uuid,
  periodId: uuid,
  /** null clears a mark that was made by mistake. */
  status: attendanceStatus.nullable(),
});

export const saveGridSchema = z.object({
  date: isoDate,
  marks: z.array(gridMarkSchema).max(2000),
});

// --- reports ---------------------------------------------------------------

export const reportQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  classId: uuid.optional(),
  studentId: uuid.optional(),
  periodId: uuid.optional(),
  groupBy: z.enum(['student', 'class', 'period', 'date']).default('student'),
});

export const reportRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  present: z.int(),
  absent: z.int(),
  late: z.int(),
  excused: z.int(),
  /** (present + late) / (present + late + absent); null when the denominator is 0. */
  rate: z.number().nullable(),
});

export const reportResponseSchema = z.object({
  from: isoDate,
  to: isoDate,
  groupBy: z.enum(['student', 'class', 'period', 'date']),
  rows: z.array(reportRowSchema),
  totals: reportRowSchema.omit({ key: true, label: true }),
});

// --- inferred types --------------------------------------------------------

export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type LoginChoice = z.infer<typeof loginChoiceSchema>;
export type LoginOrganization = z.infer<typeof loginOrganizationSchema>;
export type AdminLoginRequest = z.infer<typeof adminLoginRequestSchema>;
export type CurrentUser = z.infer<typeof currentUserSchema>;
export type CreateOrganization = z.infer<typeof createOrganizationSchema>;
export type CreateUser = z.infer<typeof createUserSchema>;
export type UpdateUser = z.infer<typeof updateUserSchema>;
export type PlatformCreateUser = z.infer<typeof platformCreateUserSchema>;
export type PlatformUpdateUser = z.infer<typeof platformUpdateUserSchema>;
export type ClaimSetup = z.infer<typeof claimSetupSchema>;
export type CreateStudent = z.infer<typeof createStudentSchema>;
export type UpdateStudent = z.infer<typeof updateStudentSchema>;
export type CreateClass = z.infer<typeof createClassSchema>;
export type UpdateClass = z.infer<typeof updateClassSchema>;
export type CreatePeriod = z.infer<typeof createPeriodSchema>;
export type UpdatePeriod = z.infer<typeof updatePeriodSchema>;
export type UpdateOrganization = z.infer<typeof updateOrganizationSchema>;
export type UpdateClassPeriod = z.infer<typeof updateClassPeriodSchema>;
export type AddClassPeriod = z.infer<typeof addClassPeriodSchema>;
export type AddClassTeacher = z.infer<typeof addClassTeacherSchema>;
export type UpdateClassSession = z.infer<typeof updateClassSessionSchema>;
export type ExceptionQuery = z.infer<typeof exceptionQuerySchema>;
export type CreateEnrollment = z.infer<typeof createEnrollmentSchema>;
export type BulkEnrollment = z.infer<typeof bulkEnrollmentSchema>;
export type CreateSchedule = z.infer<typeof createScheduleSchema>;
export type UpdateSchedule = z.infer<typeof updateScheduleSchema>;
export type CreateException = z.infer<typeof createExceptionSchema>;
export type GridResponse = z.infer<typeof gridResponseSchema>;
export type GridPeriod = z.infer<typeof gridPeriodSchema>;
export type GridStudent = z.infer<typeof gridStudentSchema>;
export type GridMark = z.infer<typeof gridMarkSchema>;
export type SaveGrid = z.infer<typeof saveGridSchema>;
export type ReportQuery = z.infer<typeof reportQuerySchema>;
export type ReportRow = z.infer<typeof reportRowSchema>;
export type ReportResponse = z.infer<typeof reportResponseSchema>;

/** Key used by `GridResponse.cells`. Kept here so API and UI agree on the shape. */
export function cellKey(studentId: string, periodId: string): string {
  return `${studentId}:${periodId}`;
}
